import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useConfirmationContext } from "@contexts/ConfirmationContext";
import { Scene as SceneType } from "@ltypes/scene";
import GroupType from "@ltypes/group";
import ProductService from "@services/ProductService";
import Loader from "@components/ui/Loader/Loader";
import Scene from "@components/scenes/Scene/Scene";
import CreateScene from "@components/organisms/CreateScene/CreateScene";
import Alert from "@components/ui/Alert/Alert";
import { MdWifiOff } from "react-icons/md";
import Button from "@components/forms/Button/Button";
import { FaChevronLeft } from "react-icons/fa6";

type ScenesPageParams = {
    productId: string,
    groupId: string,
};

function ScenesPage() {
    const params = useParams<ScenesPageParams>();

    const [isLoading, setLoading] = useState<boolean>(true);
    const [group, setGroup] = useState<GroupType>();
    const [lastSceneId, setLastSceneId] = useState<string>();
    const [isConnected, setConnected] = useState<boolean>(true);

    const { t } = useTranslation();
    const navigate = useNavigate();

    const { openConfirmationModal } = useConfirmationContext();

    const service = new ProductService;

    // Each persisted change pushes the full config to the device. A single
    // debounced request persists the whole scene state of the group (order +
    // enabled) at once, so any burst of clicks — reordering, enabling/disabling
    // one or several scenes — results in exactly one push once it settles. The
    // local state stays optimistic (instant) in the meantime.
    const DEBOUNCE_MS = 700;
    const flushTimer = useRef<ReturnType<typeof setTimeout>|null>(null);
    const scenesRef = useRef<Array<SceneType>|null>(null);   // latest optimistic scenes
    const snapshotRef = useRef<Array<SceneType>|null>(null); // scenes before the burst (rollback)

    // Keep a live mirror of the current scenes so the debounced flush always
    // reads the latest optimistic state.
    useEffect(() => {
        scenesRef.current = group ? group.scenes : null;
    }, [group]);

    // Clear any pending flush when leaving the page.
    useEffect(() => {
        return () => {
            if(flushTimer.current)
                clearTimeout(flushTimer.current);
        };
    }, []);

    const scheduleScenesFlush = () => {
        if(!params.productId || !group)
            return;

        const productId: string = params.productId;
        const groupId: string = group.id;

        // Snapshot the state at the start of the burst for rollback on failure.
        if(snapshotRef.current === null)
            snapshotRef.current = group.scenes;

        if(flushTimer.current)
            clearTimeout(flushTimer.current);

        flushTimer.current = setTimeout(() => {
            flushTimer.current = null;
            const scenes = scenesRef.current;
            const snapshot = snapshotRef.current;
            snapshotRef.current = null;
            if(!scenes)
                return;

            const promise = service.syncScenes(productId, groupId, scenes.map((s: SceneType) => ({ id: s.id, enabled: s.enabled })));
            promise.then(response => {
                setGroup((oldGroup: GroupType|undefined) => {
                    if(!oldGroup) return oldGroup;
                    return {...oldGroup, scenes: response.body};
                });
            }).catch(() => {
                // Revert to the pre-burst state on failure.
                setGroup((oldGroup: GroupType|undefined) => {
                    if(!oldGroup) return oldGroup;
                    return {...oldGroup, scenes: snapshot ?? oldGroup.scenes};
                });
            });
            toast.promise(promise, {
                loading: t('toast.updating'),
                success: data => t('toast.updated'),
                error: err => err.body && err.body.message || t('toast.error_fetching'),
            });
        }, DEBOUNCE_MS);
    };

    const handleChange = (sceneId: string, field: string, value: any) => {
        if(!group)
            return;
        
        setGroup((oldGroup: GroupType|undefined) => {
            if(oldGroup === undefined)
                return oldGroup;

            return {
                ...oldGroup,
                scenes: oldGroup.scenes.map((scene: SceneType): SceneType => {
                    return scene.id === sceneId ? {...scene, [field]: value} : scene;
                }),
            };
        });
    };

    const handleSave = (scene: SceneType) => {
        if(!params.productId || !group)
            return;

        const promise = service.updateScene(params.productId, scene);
        promise.then(response => {
            setGroup((oldGroup: GroupType|undefined) => {
                if(!oldGroup) return oldGroup;
                return {
                    ...oldGroup,
                    scenes: oldGroup.scenes.map((s: SceneType): SceneType => {
                        return s.id === scene.id ? {...s, ...response.body} : s;
                    }),
                };
            });
        });
        toast.promise(promise, {
            loading: t('toast.updating'),
            success: data => t('toast.updated'),
            error: err => err.body && err.body.message || t('toast.error_fetching'),
        });
    };

    const handleToggleEnabled = (scene: SceneType, enabled: boolean) => {
        if(!group)
            return;

        // Optimistic update
        setGroup((oldGroup: GroupType|undefined) => {
            if(!oldGroup) return oldGroup;
            return {
                ...oldGroup,
                scenes: oldGroup.scenes.map((s: SceneType) => s.id === scene.id ? {...s, enabled} : s),
            };
        });

        scheduleScenesFlush();
    };

    const handleMove = (index: number, direction: -1|1) => {
        if(!group)
            return;

        const target: number = index + direction;
        if(target < 0 || target >= group.scenes.length)
            return;

        const newScenes: Array<SceneType> = [...group.scenes];
        [newScenes[index], newScenes[target]] = [newScenes[target], newScenes[index]];

        // Optimistic update
        setGroup({...group, scenes: newScenes});

        scheduleScenesFlush();
    };

    const handleDeleteScene = (scene: SceneType) => {
        if(!params.productId || !group)
            return;
        
        const productId: string = params.productId;
        
        openConfirmationModal(t('page.scenes.confirmation_delete'), () => {
            const promise = service.deleteScene(productId, scene.id);
            toast.promise(promise, {
                loading: t('toast.deleting'),
                success: data => t('toast.deleted'),
                error: err => err.body && err.body.message || t('toast.error_fetching'),
            });
            
            promise.then(() => {
                setGroup({
                    ...group,
                    scenes: group.scenes.filter(s => s.id !== scene.id),
                });
            }).catch(() => {
            });
        });
    };

    const handleCreateScene = (type: number) => {
        if(!params.productId || !group)
            return;

        const promise = service.createScene(params.productId, group.id, type);
        toast.promise(promise, {
            loading: t('toast.creating'),
            success: data => t('toast.created'),
            error: err => err.body && err.body.message || t('toast.error_fetching'),
        });
        
        promise.then((data: any) => {
            const newScene: SceneType = data.body;
            setLastSceneId(newScene.id);

            setGroup({
                ...group,
                scenes: [
                    ...group.scenes,
                    newScene,
                ],
            });
        }).catch(() => {
        });
    };

    useEffect(() => {
        if(!params.productId || !params.groupId)
            return;

        let connected: boolean|null = null;

        service.get(params.productId).then((data: any) => {
            setLoading(false);
            connected = data.body.is_connected;
            if(data.body.groups.length > 0) {
                setGroup(data.body.groups.find((g: GroupType) => g.id === params.groupId));
            }
        }).finally(() => {
            setLoading(false);
            if(connected === false) {
                setConnected(false);
            }
        });
    }, [params.productId]);

    return (
        <>
            <div className="p-3" style={{marginBottom: '4rem'}}>
                <Button className="gray" size="sm" onClick={() => navigate(`/m/${params.productId}/groups`)}><FaChevronLeft/> {t('page.scenes.back_to_groups')}</Button>

                <h1>{t('title.scenes')}</h1>

                {isLoading ? (
                    <Loader size="5rem"/>
                ) : !group ? (<></>) : (
                    <>
                        {!isConnected && (
                            <div className="row mt-3">
                                <div className="col-12">
                                    <Alert color="yellow">
                                        <div className="row align-items-center">
                                            <div className="col-auto">
                                                <MdWifiOff size="3rem"/>
                                            </div>
                                            <div className="col">
                                                {t('page.dashboard.offline')}
                                            </div>
                                        </div>
                                    </Alert>
                                </div>
                            </div>
                        )}
                        
                        <div className="mt-3">
                            <div className="row g-3">
                                {group && group.scenes.map((scene: SceneType, index: number) => (
                                    <div key={index} className="col-12">
                                        <Scene
                                            scene={scene}
                                            new={lastSceneId === scene.id}
                                            isFirst={index === 0}
                                            isLast={index === group.scenes.length - 1}
                                            onChange={(field: string, value: any) => handleChange(scene.id, field, value)}
                                            onSave={() => handleSave(scene)}
                                            onDelete={() => handleDeleteScene(scene)}
                                            onToggleEnabled={(enabled: boolean) => handleToggleEnabled(scene, enabled)}
                                            onMoveUp={() => handleMove(index, -1)}
                                            onMoveDown={() => handleMove(index, 1)}/>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </>
                )}

                <div className="row mt-3">
                    <div className="col-12">
                        <CreateScene productId={params.productId} onCreate={handleCreateScene}/>
                    </div>
                </div>
            </div>
        </>
    );
}

export default ScenesPage;
